/**
 * Sales Dashboard - Top Klanten overzicht met Gripp data
 * Toont Budget 2026, lopende opdrachten, offertes en facturen per klant
 * Data kan gesynchroniseerd worden naar lokale Postgres database voor snellere queries
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  LinearProgress,
  Tooltip,
  IconButton,
  Collapse,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  TextField
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  FolderOpen as FolderIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Storage as StorageIcon,
  CheckCircle as CheckCircleIcon,
  HourglassEmpty as HourglassIcon,
  Cloud as CloudIcon,
  CloudDone as CloudDoneIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { getCached, setCached } from '../../utils/pageCache';
import PageHeader from '../../ui-components/PageHeader';

// Page wrapper (FF design system)
const pageWrapperSx = { width: '100%', maxWidth: 1800, mx: 'auto', px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 };

// Format currency
const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

// Format percentage
const formatPercentage = (value, total) => {
  if (!total) return '0%';
  return `${Math.round((value / total) * 100)}%`;
};

// Format date/time
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('nl-NL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Status colors for offers
const getOfferStatusColor = (status) => {
  switch (status) {
    case 1: return 'default'; // Concept
    case 2: return 'warning'; // Verzonden
    case 3: return 'success'; // Geaccepteerd
    case 4: return 'error'; // Afgewezen
    case 5: return 'default'; // Vervallen
    default: return 'default';
  }
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon: Icon, color = '#6366f1' }) => (
  <Card sx={{ 
    height: '100%',
    background: `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
    border: `1px solid ${color}30`
  }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 0.5 }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        <Avatar sx={{ bgcolor: `${color}20`, color, width: 56, height: 56 }}>
          <Icon fontSize="large" />
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

// Project Status Colors
const getProjectStatusColor = (status) => {
  switch (status) {
    case 1: return 'primary'; // Actief
    case 2: return 'success'; // Afgerond
    case 3: return 'default'; // Gearchiveerd
    case 8: return 'warning'; // Te factureren
    case 9: return 'info'; // Wachten op
    case 10: return 'error'; // On Hold
    default: return 'default';
  }
};

// All Projects Dialog Component
const AllProjectsDialog = ({ open, onClose, projects, accountName }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Alle Projecten - {accountName}
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
          {projects.map(project => (
            <Paper key={project.id} variant="outlined" sx={{ p: 0.75, px: 1, mb: 0.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Typography variant="body2" noWrap title={`${project.number} - ${project.name}`} sx={{ flex: 1, minWidth: 0 }}>
                  <b>{project.number}</b> - {project.name}
                </Typography>
                <Typography variant="body2" color="primary" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                  {formatCurrency(project.totalAmount)}
                </Typography>
                <Chip label={project.statusName || 'Onbekend'} size="small" color={getProjectStatusColor(project.status)} />
              </Box>
            </Paper>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>
    </Dialog>
  );
};

// Client Row Component with expandable details
const ClientRow = ({ account, expanded, onToggle, navigate, onReforecastSaved, rfRound = 1 }) => {
  // Reforecast inline bewerken (nieuw budget; origineel blijft in Budget2026)
  const [rfEditing, setRfEditing] = useState(false);
  const saveReforecast = async (raw) => {
    setRfEditing(false);
    const value = String(raw).trim() === '' ? null : Number(raw);
    if (value !== null && !Number.isFinite(value)) return;
    if (((account.reforecasts || {})[rfRound] ?? null) === value) return;
    try {
      const r = await authFetch(`${BACKEND_URL}/Api/sales-dashboard/reforecast/${account.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ value, round: rfRound }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Opslaan mislukt');
      onReforecastSaved?.(account.id, d);
    } catch (e) { toast.error(`Reforecast opslaan mislukt: ${e.message}`); }
  };
  const theme = useTheme();
  const gripp = account.grippData;
  const [showAllProjects, setShowAllProjects] = useState(false);
  
  // Calculate progress towards budget based on BOTH invoices AND projects (opdrachten)
  // Projects represent confirmed work that will be invoiced
  const totalRealized = (gripp?.invoices?.totalAmount || 0) + (gripp?.projects?.totalAmount || 0);
  // Voortgang t.o.v. het reforecast (nieuwe budget) als dat er is, anders het originele budget
  const workingBudget = account.reforecast2026 ?? account.budget2026;
  const progressPercentage = workingBudget
    ? Math.min((totalRealized / workingBudget) * 100, 150) // Allow up to 150% for over-delivery
    : 0;

  return (
    <>
      <TableRow 
        hover 
        sx={{ 
          cursor: 'pointer',
          '&:hover': { bgcolor: theme.palette.action.hover }
        }}
        onClick={onToggle}
      >
        <TableCell>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {account.logo ? (
              <Avatar src={account.logo} alt={account.name} sx={{ width: 40, height: 40 }} />
            ) : (
              <Avatar sx={{ bgcolor: '#6366f1', width: 40, height: 40 }}>
                <BusinessIcon />
              </Avatar>
            )}
            <Box>
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main'
                  }
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/account/${account.id}`);
                }}
              >
                {account.name}
              </Typography>
              {account.grippId && (
                <Typography variant="caption" color="textSecondary">
                  Gripp #{account.grippId}
                </Typography>
              )}
            </Box>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Typography variant="subtitle2" fontWeight={600} color="primary">
            {formatCurrency(account.budget2026)}
          </Typography>
          {rfEditing ? (
            <TextField autoFocus size="small" type="number" defaultValue={(account.reforecasts || {})[rfRound] ?? account.reforecast2026 ?? ''}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveReforecast(e.target.value);
                if (e.key === 'Escape') setRfEditing(false);
              }}
              onBlur={(e) => saveReforecast(e.target.value)}
              sx={{ width: 110, '& input': { py: 0.25, fontSize: 13, textAlign: 'right' } }} />
          ) : (
            <Tooltip title={[
              `Klik om reforecast-ronde ${rfRound} in te vullen (leegmaken = ronde wissen)`,
              ...[1, 2, 3].filter((n) => (account.reforecasts || {})[n] != null)
                .map((n) => `RF${n}: ${formatCurrency(account.reforecasts[n])}`),
              'De laatst gevulde ronde geldt als actueel budget; het origineel blijft bewaard.',
            ].join(' · ')}>
              <Typography variant="caption" onClick={(e) => { e.stopPropagation(); setRfEditing(true); }}
                sx={{ cursor: 'pointer', display: 'block', fontWeight: 700,
                  color: account.reforecast2026 != null ? '#7c3aed' : '#c4b5fd' }}>
                {account.reforecast2026 != null
                  ? `RF${[3, 2, 1].find((n) => (account.reforecasts || {})[n] != null) || ''} ${formatCurrency(account.reforecast2026)}`
                  : `+ reforecast (r${rfRound})`}
              </Typography>
            </Tooltip>
          )}
        </TableCell>
        <TableCell align="right">
          <Box>
            <Typography variant="subtitle2" fontWeight={600} color="secondary">
              {formatCurrency(account.forecast2026?.totalProjectValue || 0)}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {account.forecast2026?.projectCount || 0} projecten
            </Typography>
          </Box>
        </TableCell>
        <TableCell>
          <Box sx={{ width: '100%', minWidth: 150 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" title="Facturen + Opdrachten">
                {formatCurrency(totalRealized)}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {Math.round(progressPercentage)}%
              </Typography>
            </Box>
            <LinearProgress 
              variant="determinate" 
              value={Math.min(progressPercentage, 100)}
              sx={{ 
                height: 8, 
                borderRadius: 4,
                bgcolor: '#e0e0e0',
                '& .MuiLinearProgress-bar': {
                  bgcolor: progressPercentage >= 100 ? '#4caf50' : 
                           progressPercentage >= 50 ? '#ff9800' : '#2196f3'
                }
              }}
            />
            {/* Show breakdown */}
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <Typography variant="caption" color="success.main" title="Gefactureerd">
                📄 {formatCurrency(gripp?.invoices?.totalAmount || 0)}
              </Typography>
              <Typography variant="caption" color="primary.main" title="Opdrachten">
                📋 {formatCurrency(gripp?.projects?.totalAmount || 0)}
              </Typography>
            </Box>
          </Box>
        </TableCell>
        <TableCell align="center">
          <Box>
            <Chip 
              label={gripp?.projects?.count || 0}
              size="small"
              color="primary"
              variant="outlined"
            />
            <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 0.5 }}>
              {formatCurrency(gripp?.projects?.totalAmount || 0)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Box>
            <Typography variant="body2" color="warning.main" fontWeight={500}>
              {gripp?.offers?.openCount || 0} open
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {formatCurrency(gripp?.offers?.openTotal || 0)}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: '#7c3aed' }}
              title="Gewogen pipeline: bedrag × winkans-% van de sales-fase (Kennis gemaakt 10% · Presenteren 25% · Voorstel 50% · Onderhandeling 75%)">
              {gripp?.offers?.weightedTotal != null && gripp?.offers?.openCount > 0
                ? `⚖ ${formatCurrency(gripp.offers.weightedTotal)}` : ''}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right">
          <Box>
            <Typography variant="body2" fontWeight={500}>
              {formatCurrency(gripp?.invoices?.totalAmount || 0)}
            </Typography>
            <Typography variant="caption" color={gripp?.invoices?.openAmount > 0 ? 'error.main' : 'textSecondary'}>
              {gripp?.invoices?.openAmount > 0 ? `${formatCurrency(gripp.invoices.openAmount)} open` : 'Alles betaald'}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      
      {/* Expanded Details */}
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, bgcolor: theme.palette.grey[50] }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ p: 3 }}>
              {account.error ? (
                <Alert severity="warning">{account.error}</Alert>
              ) : gripp ? (
                <Grid container spacing={3}>
                  {/* Open Offertes */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DescriptionIcon fontSize="small" color="warning" />
                      Offertes ({gripp.offers.items.length})
                      <Box component="span" sx={{ ml: 'auto', color: 'text.secondary', fontWeight: 600 }}>
                        {formatCurrency(gripp.offers.openTotal || 0)}
                        {gripp.offers.weightedTotal != null && gripp.offers.items.length > 0
                          ? ` · ⚖ ${formatCurrency(gripp.offers.weightedTotal)}` : ''}
                      </Box>
                    </Typography>
                    {gripp.offers.items.length > 0 ? (
                      <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                        {gripp.offers.items.slice(0, 10).map(offer => (
                          <Paper key={offer.id} sx={{ p: 0.75, px: 1, mb: 0.5, bgcolor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" fontWeight={600}>{offer.number}</Typography>
                              <Typography variant="caption" color="textSecondary" noWrap title={offer.subject} sx={{ flex: 1, minWidth: 0 }}>
                                {offer.subject}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} color="primary" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(offer.totalAmount)}
                              </Typography>
                              <Chip label={offer.statusName} size="small" color={getOfferStatusColor(offer.status)} />
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Geen offertes
                      </Typography>
                    )}
                  </Grid>

                  {/* Lopende Projecten */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FolderIcon fontSize="small" color="primary" />
                      Lopende Projecten ({gripp.projects.count})
                      <Box component="span" sx={{ ml: 'auto', color: 'text.secondary', fontWeight: 600 }}>
                        {formatCurrency(gripp.projects.totalAmount || 0)}
                      </Box>
                    </Typography>
                    {gripp.projects.items.length > 0 ? (
                      <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                        {gripp.projects.items.slice(0, 10).map(project => (
                          <Paper key={project.id} sx={{ p: 0.75, px: 1, mb: 0.5, bgcolor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" noWrap title={`${project.number} - ${project.name}`} sx={{ flex: 1, minWidth: 0 }}>
                                <b>{project.number}</b> - {project.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(project.totalAmount)}
                              </Typography>
                              <Chip label={project.statusName || 'Onbekend'} size="small" color={getProjectStatusColor(project.status)} />
                            </Box>
                          </Paper>
                        ))}
                        {gripp.projects.items.length > 10 && (
                          <Button
                            size="small"
                            onClick={() => setShowAllProjects(true)}
                            sx={{ mt: 1 }}
                          >
                            +{gripp.projects.items.length - 10} meer...
                          </Button>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Geen lopende projecten
                      </Typography>
                    )}

                    {/* All Projects Dialog */}
                    <AllProjectsDialog
                      open={showAllProjects}
                      onClose={() => setShowAllProjects(false)}
                      projects={gripp.projects.items}
                      accountName={account.name}
                    />
                  </Grid>

                  {/* Facturen */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReceiptIcon fontSize="small" color="success" />
                      Facturen ({gripp.invoices.totalCount})
                      <Box component="span" sx={{ ml: 'auto', color: 'text.secondary', fontWeight: 600 }}>
                        {formatCurrency(gripp.invoices.totalAmount || 0)}
                      </Box>
                    </Typography>
                    {gripp.invoices.items.length > 0 ? (
                      <Box sx={{ maxHeight: 420, overflow: 'auto' }}>
                        {gripp.invoices.items.slice(0, 10).map(invoice => (
                          <Paper key={invoice.id} sx={{ p: 0.75, px: 1, mb: 0.5, bgcolor: 'white' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                              <Typography variant="body2" fontWeight={600}>{invoice.number}</Typography>
                              <Typography variant="caption" color="textSecondary" noWrap title={invoice.subject || ''} sx={{ flex: 1, minWidth: 0 }}>
                                {invoice.subject || ''}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ whiteSpace: 'nowrap' }}>
                                {(invoice.date || '').slice(0, 10)}
                              </Typography>
                              <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
                                {formatCurrency(invoice.totalAmount)}
                              </Typography>
                              {invoice.isPaid ? (
                                <CheckCircleIcon color="success" sx={{ fontSize: 16 }} />
                              ) : (
                                <Tooltip title="Nog niet betaald"><HourglassIcon color="warning" sx={{ fontSize: 16 }} /></Tooltip>
                              )}
                            </Box>
                          </Paper>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        Geen facturen
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              ) : (
                <CircularProgress size={24} />
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Gripp Sync Dialog Component
const GrippSyncDialog = ({ open, onClose, onSyncComplete }) => {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [error, setError] = useState(null);

  // Fetch sync status on open
  useEffect(() => {
    if (open) {
      fetchSyncStatus();
    }
  }, [open]);

  const fetchSyncStatus = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/gripp-sync/status`);
      if (response.ok) {
        const data = await response.json();
        setSyncStatus(data);
      }
    } catch (err) {
      console.error('Failed to fetch sync status:', err);
      toast.error('Fout bij ophalen sync status');
    }
  };

  const handleSync = async (entity = 'all') => {
    setSyncing(true);
    setError(null);
    setSyncResult(null);

    try {
      const payload = { entity };
      if (entity !== 'projects') {
        payload.year = 2026;
      }

      const response = await authFetch(`${BACKEND_URL}/Api/gripp-sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || `Server error (${response.status})`);
        return;
      }

      const data = await response.json();

      if (data.success) {
        setSyncResult(data);
        await fetchSyncStatus(); // Refresh status
        if (onSyncComplete) onSyncComplete();
      } else {
        setError(data.error || 'Sync failed');
      }
    } catch (err) {
      console.error('Sync error:', err);
      setError(`Sync mislukt: ${err.message}. Controleer of de AI Engine draait.`);
    } finally {
      setSyncing(false);
    }
  };

  const getSyncStatusIcon = (status) => {
    if (status === 'completed') return <CloudDoneIcon color="success" />;
    if (status === 'running') return <SyncIcon color="primary" sx={{ animation: 'spin 1s linear infinite' }} />;
    if (status === 'failed') return <ErrorIcon color="error" />;
    return <CloudIcon color="disabled" />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorageIcon color="primary" />
        Gripp Data Synchronisatie
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
          Synchroniseer Gripp data (offertes, facturen, projecten) naar de lokale database voor snellere queries.
          Projecten worden zonder jaarfilter gesynchroniseerd zodat alle lopende projecten worden meegenomen.
        </Typography>

        {/* Sync Status */}
        {syncStatus && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Sync Status</Typography>
            <List dense>
              {['offers', 'invoices', 'projects'].map(entity => {
                const status = syncStatus[entity];
                return (
                  <ListItem key={entity} sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {getSyncStatusIcon(status?.status)}
                    </ListItemIcon>
                    <ListItemText
                      primary={entity === 'offers' ? 'Offertes' : entity === 'invoices' ? 'Facturen' : 'Projecten'}
                      secondary={
                        status?.last_sync_at 
                          ? `${status.last_sync_count || 0} records - ${formatDateTime(status.last_sync_at)}`
                          : 'Nog niet gesynchroniseerd'
                      }
                    />
                    {status?.error && (
                      <Tooltip title={status.error}>
                        <ErrorIcon color="error" fontSize="small" />
                      </Tooltip>
                    )}
                  </ListItem>
                );
              })}
            </List>
          </Box>
        )}

        {/* Sync Result */}
        {syncResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            ✅ Sync voltooid! {syncResult.results && Object.entries(syncResult.results).map(([key, val]) => 
              `${key}: ${val.synced || 0}`
            ).join(', ')}
          </Alert>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Sync Buttons */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={syncing ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            onClick={() => handleSync('all')}
            disabled={syncing}
            fullWidth
          >
            {syncing ? 'Synchroniseren...' : 'Sync Alles (2026)'}
          </Button>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSync('offers')}
              disabled={syncing}
              sx={{ flex: 1 }}
            >
              Alleen Offertes
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSync('invoices')}
              disabled={syncing}
              sx={{ flex: 1 }}
            >
              Alleen Facturen
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => handleSync('projects')}
              disabled={syncing}
              sx={{ flex: 1 }}
            >
              Alleen Projecten
            </Button>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Sluiten</Button>
      </DialogActions>

      {/* Spinning animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </Dialog>
  );
};

// Main Dashboard Component
export default function SalesDashboard() {
  const navigate = useNavigate();
  const salesCacheKey = 'sales-dashboard';
  const salesCachedInit = getCached(salesCacheKey);
  const [loading, setLoading] = useState(!salesCachedInit);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(salesCachedInit?.data || null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [rfRound, setRfRound] = useState(1);   // in welke reforecast-ronde vul je nu in?
  // Standaard ALLE klanten (matcht de Gripp-totalen; anders miste je de facturatie
  // van kleinere klanten). Keuze onthouden zodat een reload niet terugvalt.
  const [showAll, setShowAll] = useState(() => localStorage.getItem('sales-show-all') !== '0');
  const [searchQ, setSearchQ] = useState('');
  // Sorteerbare kolommen — bv. op Facturen om klanten te spotten die wél
  // factureren maar geen (of een te laag) budget in de lijst hebben.
  const [sortCol, setSortCol] = useState(null);   // null = server-volgorde (Budget 2026)
  const [sortDir, setSortDir] = useState('desc');
  const rfRoundInitialized = useRef(false);

  const showAllRef = useRef(localStorage.getItem('sales-show-all') !== '0');
  const fetchDashboard = useCallback(async (sync = false) => {
    const cached = getCached('sales-dashboard');
    if (sync) {
      setSyncing(true);
    } else if (!cached) {
      setLoading(true);   // met cache: op de achtergrond verversen, niet blokkeren
    }
    setError(null);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/sales-dashboard?limit=${showAllRef.current ? 300 : 50}&all=${showAllRef.current}&sync=${sync}`);
      if (!response.ok) throw new Error('Failed to fetch dashboard');
      const data = await response.json();
      setDashboardData(data);
      setCached('sales-dashboard', data);
      // Default invul-ronde = hoogste ronde die ergens al gevuld is (eenmalig bij laden)
      if (!rfRoundInitialized.current) {
        rfRoundInitialized.current = true;
        const highest = Math.max(1, ...(data.accounts || []).flatMap((a) =>
          [1, 2, 3].filter((n) => (a.reforecasts || {})[n] != null)));
        setRfRound(highest);
      }
      
      // Show sync result if synced
      if (sync && data.syncResult) {
        console.log(`Synced ${data.syncResult.synced} records to Airtable`);
      }
    } catch (err) {
      console.error('Error fetching sales dashboard:', err);
      toast.error('Fout bij laden sales dashboard');
      setError(err.message);
    } finally {
      setLoading(false);
      setSyncing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const toggleRow = (accountId) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(accountId)) {
        newSet.delete(accountId);
      } else {
        newSet.add(accountId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Box sx={pageWrapperSx}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={48} />
          <Typography>Sales Dashboard laden...</Typography>
          <Typography variant="caption" color="textSecondary">
            Data wordt opgehaald uit Airtable en Postgres
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={pageWrapperSx}>
        <Alert severity="error" action={
          <IconButton color="inherit" onClick={() => fetchDashboard()}>
            <RefreshIcon />
          </IconButton>
        }>
          {error}
        </Alert>
      </Box>
    );
  }

  const { accounts, totals } = dashboardData || { accounts: [], totals: {} };
  // Sorteerbare kolommen — bv. op Facturen om klanten te spotten die wél
  // factureren maar geen (of een te laag) budget in de lijst hebben.
  const SORT_METRICS = {
    klant: (a) => (a.name || '').toLowerCase(),
    budget: (a) => a.reforecast2026 ?? a.budget2026 ?? 0,
    forecast: (a) => a.forecast2026?.totalProjectValue || 0,
    voortgang: (a) => (a.grippData?.invoices?.totalAmount || 0) + (a.grippData?.projects?.totalAmount || 0),
    projecten: (a) => a.grippData?.projects?.totalAmount || 0,
    offertes: (a) => a.grippData?.offers?.openTotal || 0,
    facturen: (a) => a.grippData?.invoices?.totalAmount || 0,
  };
  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortCol(col); setSortDir(col === 'klant' ? 'asc' : 'desc'); }
  };
  const filteredAccounts = searchQ.trim()
    ? accounts.filter((a) => (a.name || '').toLowerCase().includes(searchQ.trim().toLowerCase()))
    : accounts;
  const visibleAccounts = sortCol
    ? [...filteredAccounts].sort((a, b) => {
        const va = SORT_METRICS[sortCol](a);
        const vb = SORT_METRICS[sortCol](b);
        const cmp = typeof va === 'string' ? va.localeCompare(vb) : (va - vb);
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : filteredAccounts;

  return (
    <Box sx={pageWrapperSx}>
      {/* Header */}
      <PageHeader
        icon="trending_up"
        title="Sales Dashboard"
        subtitle={showAll
          ? `Alle ${accounts.length} klanten met Gripp-koppeling${searchQ ? ` · ${visibleAccounts.length} gevonden` : ''}`
          : `Top ${accounts.length} klanten op basis van Budget 2026${searchQ ? ` · ${visibleAccounts.length} gevonden` : ''}`}
        rightActions={(
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField size="small" placeholder="Zoek klant…" value={searchQ}
            onChange={(e) => setSearchQ(e.target.value)}
            sx={{ width: 170, '& .MuiInputBase-input': { py: 0.6, fontSize: 13 } }} />
          {/* Duidelijke twee-knops-toggle i.p.v. één klikbaar woord (de scope
              bepaalt de totalen — verwarrend als 'ie onopgemerkt op Top staat). */}
          <Box sx={{ display: 'inline-flex', border: '1px solid #e5e7eb', borderRadius: 1, overflow: 'hidden', mr: 1 }}>
            {[{ v: true, label: 'Alle klanten' }, { v: false, label: 'Topklanten' }].map((o) => (
              <Box key={o.label} component="button" onClick={() => {
                if (showAll === o.v) return;
                setShowAll(o.v); showAllRef.current = o.v;
                try { localStorage.setItem('sales-show-all', o.v ? '1' : '0'); } catch { /* quota */ }
                fetchDashboard(false);
              }} sx={{
                border: 0, cursor: 'pointer', px: 1.25, py: 0.5, fontSize: 12, fontWeight: 700,
                bgcolor: showAll === o.v ? '#6d28d9' : '#fff', color: showAll === o.v ? '#fff' : '#64748b',
              }}>{o.label}</Box>
            ))}
          </Box>
          <Typography variant="caption" color="textSecondary">
            {dashboardData?.generatedAt ? `Bijgewerkt: ${new Date(dashboardData.generatedAt).toLocaleTimeString('nl-NL')}` : ''}
            {'  ·  Reforecast-ronde: '}
            {[1, 2, 3].map((n) => (
              <Box key={n} component="span" onClick={() => setRfRound(n)} sx={{
                cursor: 'pointer', px: 0.6, mx: 0.1, borderRadius: 0.75, fontWeight: 700,
                bgcolor: rfRound === n ? '#ede9fe' : 'transparent',
                color: rfRound === n ? '#6d28d9' : 'text.secondary',
              }}>{n}</Box>
            ))}
          </Typography>
          
          {/* Sync to Postgres button */}
          <Tooltip title="Sync Gripp data naar lokale database">
            <Button
              variant="outlined"
              size="small"
              startIcon={<StorageIcon />}
              onClick={() => setSyncDialogOpen(true)}
              sx={{ mr: 1 }}
            >
              Sync DB
            </Button>
          </Tooltip>
          
          <Tooltip title="Ververs data">
            <IconButton onClick={() => fetchDashboard(false)} color="primary" disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Sync naar Airtable (Offerte2026, Opdrachten2026, Facturen2026)">
            <IconButton 
              onClick={() => fetchDashboard(true)} 
              color="success"
              disabled={syncing}
              sx={{ 
                animation: syncing ? 'spin 1s linear infinite' : 'none',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' }
                }
              }}
            >
              <SyncIcon />
            </IconButton>
          </Tooltip>
        </Box>
        )}
      />

      {/* Sync result message */}
      {dashboardData?.syncResult && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setDashboardData(prev => ({ ...prev, syncResult: null }))}>
          ✅ {dashboardData.syncResult.synced} accounts gesynchroniseerd naar Airtable
          {dashboardData.syncResult.errors?.length > 0 && ` (${dashboardData.syncResult.errors.length} fouten)`}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={true}>
          <SummaryCard
            title="Budget 2026"
            value={formatCurrency(totals.totalBudget2026)}
            subtitle={totals.totalReforecast2026 && totals.totalReforecast2026 !== totals.totalBudget2026
              ? `Reforecast: ${formatCurrency(totals.totalReforecast2026)}` : 'Totaal alle klanten'}
            icon={TrendingUpIcon}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={true}>
          <SummaryCard
            title="Forecast 2026"
            value={formatCurrency(totals.totalForecast2026)}
            subtitle={`${formatPercentage(totals.totalForecast2026, totals.totalBudget2026)} van budget`}
            icon={TrendingUpIcon}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={true}>
          <SummaryCard
            title="Gefactureerd"
            value={formatCurrency(totals.totalInvoiced)}
            subtitle={`${formatPercentage(totals.totalInvoiced, totals.totalBudget2026)} van budget · ${showAll ? 'alle klanten' : 'topklanten'}`}
            icon={ReceiptIcon}
            color="#10b981"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={true}>
          <SummaryCard
            title="Open Offertes"
            value={formatCurrency(totals.totalOpenOffers)}
            subtitle={totals.totalWeightedOffers != null
              ? `⚖ ${formatCurrency(totals.totalWeightedOffers)} gewogen`
              : 'Verzonden'}
            icon={DescriptionIcon}
            color="#f59e0b"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={true}>
          <SummaryCard
            title="Opdrachten"
            value={formatCurrency(totals.totalProjectsAmount)}
            subtitle={`${totals.activeProjects} projecten`}
            icon={FolderIcon}
            color="#3b82f6"
          />
        </Grid>
      </Grid>

      {/* Verwachte omzet: hard binnen (gefactureerd + lopende opdrachten excl.
          On Hold) + gewogen offerte-pipeline — één realistisch omzetgetal. */}
      {(() => {
        const opdrachtenExclHold = (totals.totalProjectsAmount || 0) - (totals.totalProjectsOnHold || 0);
        const verwacht = (totals.totalInvoiced || 0) + opdrachtenExclHold + (totals.totalWeightedOffers || 0);
        return (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <SummaryCard
                title="Verwachte omzet 2026"
                value={formatCurrency(verwacht)}
                subtitle={`Gefactureerd ${formatCurrency(totals.totalInvoiced || 0)} · opdrachten excl. on hold ${formatCurrency(opdrachtenExclHold)} · gewogen pipeline ${formatCurrency(totals.totalWeightedOffers || 0)}`}
                icon={TrendingUpIcon}
                color="#10b981"
              />
            </Grid>
          </Grid>
        );
      })()}

      {/* Clients Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell width={50}></TableCell>
                <TableCell>
                  <TableSortLabel active={sortCol === 'klant'} direction={sortCol === 'klant' ? sortDir : 'desc'} onClick={() => handleSort('klant')}>
                    <Typography variant="subtitle2" fontWeight={600}>Klant</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortCol === 'budget'} direction={sortCol === 'budget' ? sortDir : 'desc'} onClick={() => handleSort('budget')}>
                    <Typography variant="subtitle2" fontWeight={600}>Budget 2026</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortCol === 'forecast'} direction={sortCol === 'forecast' ? sortDir : 'desc'} onClick={() => handleSort('forecast')}>
                    <Typography variant="subtitle2" fontWeight={600}>Forecast</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel active={sortCol === 'voortgang'} direction={sortCol === 'voortgang' ? sortDir : 'desc'} onClick={() => handleSort('voortgang')}>
                    <Typography variant="subtitle2" fontWeight={600}>Voortgang</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">
                  <TableSortLabel active={sortCol === 'projecten'} direction={sortCol === 'projecten' ? sortDir : 'desc'} onClick={() => handleSort('projecten')}>
                    <Typography variant="subtitle2" fontWeight={600}>Projecten</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortCol === 'offertes'} direction={sortCol === 'offertes' ? sortDir : 'desc'} onClick={() => handleSort('offertes')}>
                    <Typography variant="subtitle2" fontWeight={600}>Offertes</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel active={sortCol === 'facturen'} direction={sortCol === 'facturen' ? sortDir : 'desc'} onClick={() => handleSort('facturen')}>
                    <Typography variant="subtitle2" fontWeight={600}>Facturen</Typography>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleAccounts.map((account) => (
                <ClientRow
                  key={account.id}
                  account={account}
                  expanded={expandedRows.has(account.id)}
                  onToggle={() => toggleRow(account.id)}
                  navigate={navigate}
                  rfRound={rfRound}
                  onReforecastSaved={(id, d) => setDashboardData((prev) => (prev ? {
                    ...prev,
                    accounts: (prev.accounts || []).map((a) => (a.id === id
                      ? { ...a, reforecasts: d.reforecasts || a.reforecasts, reforecast2026: d.reforecast2026 ?? null }
                      : a)),
                  } : prev))}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Gripp Sync Dialog */}
      <GrippSyncDialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        onSyncComplete={() => {
          // Optionally refresh dashboard after sync
          console.log('Gripp sync completed');
        }}
      />
    </Box>
  );
}
